import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { NavLink } from 'react-router-dom';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import { EmptyState, PageHeader, useNotify } from '@routes-plan/ui';
import { Office } from '../../../models/office';
import { VisitOrder } from '../../../models/visit-order';
import { Route } from '../../../models/route';
import useAxiosInterceptor from '../../AxiosInterceptor';
import PaymentModal from '../../loans/modal/PaymentModal';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import StandardModal, { useModalLoading } from '../../../components/modales/StandardModal';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import { nowOperationalDateString } from '../../../utils/operationalDateTime';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { formatCurrency as companyMoney } from '../../../utils/format/currency';
import { FilterBar, OfficeRouteFields } from '../components/ProcessFilters';
import { ScreenTourButton } from '../../../components/help/ScreenHelp';

type Severity = 'success' | 'info' | 'warning' | 'error';

export interface ReceiptProps {
  isOpen: boolean;
  onClose: () => void;
  clientName: string;
  showAlert: (message: string, severity?: Severity) => void;
  showLoading: () => void;
  hideLoading: () => void;
  loanId?: number;
}

interface Props {
  showLoading: () => void;
  hideLoading: () => void;
  /** Recibo que se abre al imprimir (en línea o sin conexión). */
  Receipt: React.FC<ReceiptProps>;
}

type Tone = 'info' | 'warning' | 'success';

/**
 * Orden de visitas del día: elige sede y ruta, y ve los préstamos por visitar, pendientes y
 * completados. Lo usan la pantalla normal y la de sin conexión (cambia solo el recibo).
 */
const VisitOrderBoard: React.FC<Props> = ({ showLoading, hideLoading, Receipt }) => {
  const t = useT();
  const notify = useNotify();
  const showAlert = (message: string, severity: Severity = 'info') => notify[severity](message);
  useEffect(() => {
    setPageTitle('visitOrders.title');
  }, []);
  const [officeId, setOfficeId] = useState('');
  const [selectedRouteId, setSelectedRouteId] = useState<string>('');
  const [officeRoutes, setOfficeRoutes] = useState<Route[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [routes, setRoutes] = useState<VisitOrder[]>([]);
  const [pendingRoutes, setPendingRoutes] = useState<VisitOrder[]>([]);
  const [completedRoutes, setCompletedRoutes] = useState<VisitOrder[]>([]);
  const pendingRef = useRef<VisitOrder[]>([]);
  pendingRef.current = pendingRoutes;
  const [visitOrderId, setVisitOrderId] = useState('');
  const [tabIndex, setTabIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState<number | null>(null);
  const [modalInvoiceOpen, setModalInvoiceOpen] = useState(false);
  const [clientFullName, setClientFullName] = useState('');
  const invoiceLoading = useModalLoading();
  // Pagar registra un abono nuevo con las reglas de Abonos y préstamos (permiso Pagar de ese menú).
  const {
    canPay,
    canAllowDiscount,
    canAllowManualDiscount,
    canAllowAutomaticDiscount,
    canPdf,
    authUserData,
  } = useResourcePermissions('/loans/payments');
  const [reload, setReload] = useState(0);
  useAxiosInterceptor();

  // Sedes, rutas de la sede y orden de visitas de la ruta elegida.
  useEffect(() => {
    const fetchData = async () => {
      showLoading();
      try {
        const officesResponse = axios.get('offices');
        const officeRoutesResponse = officeId ? axios.get(`offices/${officeId}/routes`) : null;
        const visitOrdersResponse = selectedRouteId ? axios.get(`routes/${selectedRouteId}/visit-orders`) : null;

        const [officesData, officeRoutesData, visitOrdersData] = await Promise.all([
          officesResponse,
          officeRoutesResponse,
          visitOrdersResponse,
        ]);

        if (visitOrdersData && visitOrdersData.data.data && visitOrdersData.data.data.length > 0) {
          setVisitOrderId(visitOrdersData.data.data[0].id);
        }
        if (officesData) setOffices(officesData.data.data ?? []);
        if (officeRoutesData) setOfficeRoutes(Array.isArray(officeRoutesData.data) ? officeRoutesData.data : []);
      } catch (error) {
        // Sin datos: la pantalla queda con su estado vacío.
      } finally {
        hideLoading();
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRouteId, officeId]);

  // Préstamos del orden de visitas.
  useEffect(() => {
    if (String(visitOrderId).length === 0) return;
    const fetchRoutes = async () => {
      showLoading();
      try {
        const { data } = await axios.get(`visit-orders/${visitOrderId}/loans`);
        const active = data.data.filter((route: VisitOrder) => Number(route.loan.state) === 0);
        // Los marcados como pendientes siguen en Pendientes al recargar (si aún deben).
        const pendingIds = new Set(pendingRef.current.map((p) => p.loan.id));
        setPendingRoutes(active.filter((r: VisitOrder) => pendingIds.has(r.loan.id)));
        setRoutes(active.filter((r: VisitOrder) => !pendingIds.has(r.loan.id)));
        setCompletedRoutes(data.data.filter((route: VisitOrder) => Number(route.loan.state) === 1));
        showAlert(t('visitOrders.loadedOk'), 'info');
      } catch (error) {
        showAlert(t('common.loadError'), 'error');
      } finally {
        hideLoading();
      }
    };
    fetchRoutes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitOrderId, reload]);

  const moveToPending = (route: VisitOrder) => {
    setRoutes((prev) => prev.filter((r) => r.loan.id !== route.loan.id));
    setPendingRoutes((prev) => [...prev, route]);
  };

  const clearRoutes = () => {
    setRoutes([]);
    setPendingRoutes([]);
    setCompletedRoutes([]);
    setVisitOrderId('');
  };

  const today = nowOperationalDateString();

  const card = (route: VisitOrder, tone: Tone, actions: React.ReactNode) => {
    const client = route.loan.client;
    return (
      <Paper
        key={route.loan.id}
        variant="outlined"
        component="article"
        sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0, height: '100%' }}
      >
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', minWidth: 0 }}>
          <Avatar sx={{ bgcolor: `${tone}.main`, color: `${tone}.contrastText`, width: 40, height: 40, fontWeight: 600 }}>
            {client.fullName.charAt(0)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600} noWrap title={client.fullName}>
              {client.fullName}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {tone === 'success' ? t('visitOrders.receiptShort', { id: route.loan.id }) : today}
            </Typography>
          </Box>
        </Box>
        <Stack spacing={0.5} sx={{ color: 'text.secondary', minWidth: 0 }}>
          {[
            { icon: <BadgeOutlinedIcon fontSize="small" />, text: client.idNumber, label: t('visitOrders.clientIdNumber') },
            { icon: <PhoneOutlinedIcon fontSize="small" />, text: client.phone, label: t('common.phone') },
            { icon: <PlaceOutlinedIcon fontSize="small" />, text: client.mainAddress, label: t('common.address') },
          ].map((item) => (
            <Box key={item.label} sx={{ display: 'flex', gap: 1, alignItems: 'center', minWidth: 0 }}>
              <Box component="span" aria-label={item.label} sx={{ display: 'inline-flex' }}>
                {item.icon}
              </Box>
              <Typography variant="body2" noWrap title={String(item.text ?? '')} sx={{ minWidth: 0 }}>
                {item.text || '—'}
              </Typography>
            </Box>
          ))}
        </Stack>
        <Box
          component="dl"
          sx={{
            m: 0,
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 1,
            p: 1.5,
            borderRadius: 1,
            bgcolor: 'action.hover',
            '& dt': { typography: 'caption', color: 'text.secondary' },
            '& dd': { m: 0, typography: 'body2', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
          }}
        >
          <Box>
            <dt>{t('visitOrders.installmentLabel')}</dt>
            <dd>{companyMoney(route.loan.installmentValue)}</dd>
          </Box>
          <Box>
            <dt>{t('visitOrders.requestedLabel')}</dt>
            <dd>{companyMoney(route.loan.value)}</dd>
          </Box>
          <Box>
            <dt>{t('visitOrders.totalLabel')}</dt>
            <dd>{companyMoney(route.loan.totalValue)}</dd>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1, mt: 'auto' }}>{actions}</Box>
      </Paper>
    );
  };

  const payButton = (route: VisitOrder) => (
    <PermissionButton
      allowed={canPay}
      deniedTooltip={t('payments.noPermissionPay')}
      variant="contained"
      data-tour="visit-orders-pay"
      onClick={() => {
        setSelectedPaymentId(route.loan.id);
        setModalOpen(true);
      }}
    >
      {t('visitOrders.pay')}
    </PermissionButton>
  );

  const tabs: { key: string; label: string; count: number; tone: Tone; items: VisitOrder[]; empty: string }[] = [
    { key: 'onRoute', label: t('visitOrders.tabOnRoute'), count: routes.length, tone: 'info', items: routes, empty: t('visitOrders.emptyOnRoute') },
    { key: 'pending', label: t('visitOrders.tabPending'), count: pendingRoutes.length, tone: 'warning', items: pendingRoutes, empty: t('visitOrders.emptyPending') },
    { key: 'done', label: t('visitOrders.tabCompleted'), count: completedRoutes.length, tone: 'success', items: completedRoutes, empty: t('visitOrders.emptyCompleted') },
  ];
  const current = tabs[tabIndex];

  return (
    <HeaderResponsive>
      <PageHeader
        title={t('visitOrders.title')}
        description={t('visitOrders.subtitle')}
        helpAction={<ScreenTourButton />}
        actions={
          <Button variant="contained" startIcon={<AddOutlinedIcon />} component={NavLink} to="/loans/new" data-tour="visit-orders-new-loan">
            {t('visitOrders.generate')}
          </Button>
        }
      />

      <FilterBar>
        <OfficeRouteFields
          idPrefix="visit-orders"
          tourPrefix="visit-orders"
          offices={offices}
          routes={officeRoutes}
          officeId={officeId}
          routeId={selectedRouteId}
          onOfficeChange={(v) => {
            setOfficeId(v);
            setSelectedRouteId('');
            clearRoutes();
          }}
          onRouteChange={(v) => {
            setSelectedRouteId(v);
            clearRoutes();
          }}
        />
      </FilterBar>

      <Paper variant="outlined" sx={{ minWidth: 0 }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          aria-label={t('visitOrders.tabsLabel')}
          data-tour="visit-orders-tabs"
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.key}
              label={
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                  {tab.label}
                  <Box
                    component="span"
                    sx={{
                      minWidth: 22,
                      px: 0.75,
                      borderRadius: 10,
                      typography: 'caption',
                      fontWeight: 600,
                      bgcolor: tab.count > 0 ? `${tab.tone}.main` : 'action.selected',
                      color: tab.count > 0 ? `${tab.tone}.contrastText` : 'text.secondary',
                    }}
                  >
                    {tab.count}
                  </Box>
                </Box>
              }
            />
          ))}
        </Tabs>
        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
          {current.items.length > 0 ? (
            <Box
              data-tour="visit-orders-cards"
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))',
              }}
            >
              {current.items.map((route) =>
                tabIndex === 0
                  ? card(
                      route,
                      current.tone,
                      <>
                        <Button variant="outlined" color="inherit" onClick={() => moveToPending(route)} data-tour="visit-orders-mark-pending">
                          {t('visitOrders.markPending')}
                        </Button>
                        {payButton(route)}
                      </>,
                    )
                  : tabIndex === 1
                    ? card(route, current.tone, payButton(route))
                    : card(
                        route,
                        current.tone,
                        <Button
                          variant="outlined"
                          data-tour="visit-orders-print"
                          onClick={() => {
                            setClientFullName(route.loan.client.fullName);
                            setSelectedPaymentId(route.loan.id);
                            setModalInvoiceOpen(true);
                          }}
                        >
                          {t('visitOrders.print')}
                        </Button>,
                      ),
              )}
            </Box>
          ) : (
            <EmptyState
              dense
              icon={<RouteOutlinedIcon />}
              title={current.empty}
              description={selectedRouteId ? undefined : t('visitOrders.selectToStart')}
            />
          )}
        </Box>
      </Paper>

      <PaymentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        client={null}
        clientId={selectedPaymentId ?? undefined}
        visitOrderId={Number(visitOrderId) || null}
        currentUserId={authUserData?.id}
        canAllowDiscount={canAllowDiscount}
        canAllowManualDiscount={canAllowManualDiscount}
        canAllowAutomaticDiscount={canAllowAutomaticDiscount}
        canPdf={canPdf}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
        onPaymentSuccess={() => setReload((n) => n + 1)}
      />
      <StandardModal
        open={modalInvoiceOpen}
        onClose={() => setModalInvoiceOpen(false)}
        title={t('visitOrders.receiptTitle')}
        subtitle={clientFullName || undefined}
        maxWidth={480}
        loading={invoiceLoading.loading}
      >
        <Receipt
          showAlert={showAlert}
          showLoading={invoiceLoading.showLoading}
          hideLoading={invoiceLoading.hideLoading}
          isOpen={modalInvoiceOpen}
          onClose={() => setModalInvoiceOpen(false)}
          clientName={clientFullName}
          loanId={selectedPaymentId ?? undefined}
        />
      </StandardModal>
    </HeaderResponsive>
  );
};

export default VisitOrderBoard;
